import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface EnrollmentDataItem {
  class: string;
  currentEnrollment: number;
  expectedEnrollment: number;
}

interface EnrollmentChartViewProps {
  data: EnrollmentDataItem[];
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
}

// Enrollment-specific color scheme
const ENROLLMENT_COLORS = {
  current: 'rgba(54, 162, 235, 0.8)',
  expected: 'rgba(255, 99, 132, 0.8)',
  currentBorder: 'rgba(54, 162, 235, 1)',
  expectedBorder: 'rgba(255, 99, 132, 1)',
};

const EnrollmentChartView: React.FC<EnrollmentChartViewProps> = ({
  data,
  title = 'Class-wise Enrollment Comparison',
  xAxisLabel = 'Class',
  yAxisLabel = 'Number of Students',
}) => {
  const [selectedChartType, setSelectedChartType] = useState<'bar' | 'line' | 'area' | 'pie'>('bar');

  // Transform data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map((item) => ({
      name: item.class,
      currentEnrollment: Number(item.currentEnrollment || 0),
      expectedEnrollment: Number(item.expectedEnrollment || 0),
    }));
  }, [data]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        totalClasses: 0,
        totalCurrentEnrollment: 0,
        totalExpectedEnrollment: 0,
        averageCurrentEnrollment: 0,
        averageExpectedEnrollment: 0,
        enrollmentGap: 0,
      };
    }

    const totalCurrent = chartData.reduce((sum, item) => sum + item.currentEnrollment, 0);
    const totalExpected = chartData.reduce((sum, item) => sum + item.expectedEnrollment, 0);
    const totalClasses = chartData.length;

    return {
      totalClasses,
      totalCurrentEnrollment: totalCurrent,
      totalExpectedEnrollment: totalExpected,
      averageCurrentEnrollment: Math.round(totalCurrent / totalClasses),
      averageExpectedEnrollment: Math.round(totalExpected / totalClasses),
      enrollmentGap: totalExpected - totalCurrent,
    };
  }, [chartData]);

  // Prepare data for pie chart (aggregate current vs expected)
  const pieData = useMemo(() => {
    return [
      { name: 'Current Enrollment', value: stats.totalCurrentEnrollment, color: ENROLLMENT_COLORS.current },
      { name: 'Expected Enrollment', value: stats.totalExpectedEnrollment, color: ENROLLMENT_COLORS.expected },
    ];
  }, [stats]);

  // Custom tooltip for bar, line, and area charts
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-1">{payload[0].payload.name}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="font-medium" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const percent = ((payload[0].value / (stats.totalCurrentEnrollment + stats.totalExpectedEnrollment)) * 100).toFixed(1);
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{payload[0].name}</p>
          <p className="text-purple-600 font-medium">
            Count: {payload[0].value.toLocaleString()}
          </p>
          <p className="text-gray-600 text-sm">
            {percent}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  // Render label for pie chart
  const renderPieLabel = (entry: any) => {
    const total = stats.totalCurrentEnrollment + stats.totalExpectedEnrollment;
    const percent = ((entry.value / total) * 100).toFixed(1);
    
    if (parseFloat(percent) < 3) return '';
    
    return `${entry.name.split(' ')[0]}\n${percent}%`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header with title and chart type selector */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          {title}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedChartType('bar')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'bar'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Bar Chart"
          >
            📊 Bar
          </button>
          <button
            onClick={() => setSelectedChartType('line')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'line'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Line Chart"
          >
            📈 Line
          </button>
          <button
            onClick={() => setSelectedChartType('area')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'area'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Area Chart"
          >
            📉 Area
          </button>
          <button
            onClick={() => setSelectedChartType('pie')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'pie'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Pie Chart"
          >
            🥧 Pie
          </button>
        </div>
      </div>

      {/* Chart Container */}
      <div className="w-full" style={{ minHeight: '500px' }}>
        {selectedChartType === 'bar' && (
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 140 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: xAxisLabel, offset: -10, position: 'insideBottom', style: { fill: '#666', fontWeight: 500 } }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: '#666', fontWeight: 500 } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                verticalAlign="top"
              />
              <Bar 
                dataKey="currentEnrollment" 
                name="Current Enrollment" 
                fill={ENROLLMENT_COLORS.current}
                radius={[8, 8, 0, 0]} 
              />
              <Bar 
                dataKey="expectedEnrollment" 
                name="Expected Enrollment" 
                fill={ENROLLMENT_COLORS.expected}
                radius={[8, 8, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        )}

        {selectedChartType === 'line' && (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: xAxisLabel, offset: -10, position: 'insideBottom', style: { fill: '#666', fontWeight: 500 } }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: '#666', fontWeight: 500 } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                verticalAlign="top"
              />
              <Line
                type="monotone"
                dataKey="currentEnrollment"
                name="Current Enrollment"
                stroke={ENROLLMENT_COLORS.currentBorder}
                strokeWidth={3}
                dot={{ fill: ENROLLMENT_COLORS.current, r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                dataKey="expectedEnrollment"
                name="Expected Enrollment"
                stroke={ENROLLMENT_COLORS.expectedBorder}
                strokeWidth={3}
                dot={{ fill: ENROLLMENT_COLORS.expected, r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {selectedChartType === 'area' && (
          <ResponsiveContainer width="100%" height={600}>
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 140 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={140}
                interval={0}
                tick={{ fontSize: 12, fill: '#666', dy: 10 }}
                label={{ value: xAxisLabel, position: 'insideBottom', style: { fill: '#666', fontWeight: 500, textAnchor: 'middle' }, dy: 50 }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: '#666', fontWeight: 500 } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                verticalAlign="top"
              />
              <Area
                type="monotone"
                dataKey="currentEnrollment"
                name="Current Enrollment"
                fill={ENROLLMENT_COLORS.current}
                stroke={ENROLLMENT_COLORS.currentBorder}
                strokeWidth={2}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="expectedEnrollment"
                name="Expected Enrollment"
                fill={ENROLLMENT_COLORS.expected}
                stroke={ENROLLMENT_COLORS.expectedBorder}
                strokeWidth={2}
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {selectedChartType === 'pie' && (
          <ResponsiveContainer width="100%" height={500}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={renderPieLabel}
                outerRadius={150}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <Legend 
                verticalAlign="bottom"
                height={36}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-500">Current Enrollment</p>
            <p className="text-2xl font-bold text-purple-600">{stats.totalCurrentEnrollment.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">
              Avg: {stats.averageCurrentEnrollment.toLocaleString()} per class
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Expected Enrollment</p>
            <p className="text-2xl font-bold text-purple-600">{stats.totalExpectedEnrollment.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">
              Avg: {stats.averageExpectedEnrollment.toLocaleString()} per class
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Enrollment Gap</p>
            <p className={`text-2xl font-bold ${
              stats.enrollmentGap > 0 
                ? 'text-orange-600' 
                : stats.enrollmentGap < 0 
                ? 'text-green-600' 
                : 'text-purple-600'
            }`}>
              {stats.enrollmentGap > 0 ? '+' : ''}{stats.enrollmentGap.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {stats.enrollmentGap > 0 
                ? 'Shortage' 
                : stats.enrollmentGap < 0 
                ? 'Surplus' 
                : 'Balanced'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentChartView;
